import { Routes, Route, useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import DashboardShell from '../ui/DashboardShell'
import DashboardHome from './DashboardHome'
import JobsPanel from './JobsPanel'
import MachinesPanel from './MachinesPanel'
import ToolLibrariesPanel from './ToolLibrariesPanel'
import UsagePanel from './UsagePanel'
import type { MachineProfile, Tool } from '../../types'

interface DashboardProps {
  onReopenJob: (id: string) => void
  onDeleteJob: (id: string) => void
  profiles: MachineProfile[]
  onAddProfile: (p: MachineProfile) => void
  onUpdateProfile: (id: string, updates: Partial<MachineProfile>) => void
  onRemoveProfile: (id: string) => void
  tools: Tool[]
  onChangeTools: (tools: Tool[]) => void
}

export default function Dashboard({
  onReopenJob,
  onDeleteJob,
  profiles,
  onAddProfile,
  onUpdateProfile,
  onRemoveProfile,
  tools,
  onChangeTools,
}: DashboardProps) {
  const navigate = useNavigate()

  const newProjectButton = (
    <button
      data-tour="new-job"
      onClick={() => navigate('/new-job')}
      className="
        flex items-center gap-2 px-4 py-2 rounded-lg
        font-mono text-sm font-semibold
        bg-cyan-500/15 border border-cyan-500/40 text-cyan-400
        hover:bg-cyan-500/25 hover:border-cyan-400/60
        transition-all duration-200
      "
      style={{ boxShadow: '0 0 16px rgba(6,182,212,0.15)' }}
    >
      <Plus className="w-4 h-4" />
      New Project
    </button>
  )

  return (
    <Routes>
      <Route
        index
        element={
          <DashboardShell
            title="DASHBOARD"
            subtitle="Manage projects, machines, and tools."
            headerAction={newProjectButton}
          >
            <DashboardHome />
          </DashboardShell>
        }
      />
      <Route
        path="projects"
        element={
          <DashboardShell
            title="PROJECTS"
            subtitle="Your G-code generation projects."
            backTo={{ label: 'Dashboard', path: '/dashboard' }}
            headerAction={newProjectButton}
          >
            <JobsPanel onReopenJob={onReopenJob} onDeleteJob={onDeleteJob} />
          </DashboardShell>
        }
      />
      <Route
        path="machines"
        element={
          <DashboardShell
            title="MACHINES"
            subtitle="Machine profiles and configurations."
            backTo={{ label: 'Dashboard', path: '/dashboard' }}
          >
            <MachinesPanel
              profiles={profiles}
              onAdd={onAddProfile}
              onUpdate={onUpdateProfile}
              onRemove={onRemoveProfile}
            />
          </DashboardShell>
        }
      />
      <Route
        path="tools"
        element={
          <DashboardShell
            title="TOOLS"
            subtitle="Tool inventory and libraries."
            backTo={{ label: 'Dashboard', path: '/dashboard' }}
          >
            <ToolLibrariesPanel tools={tools} onChangeTools={onChangeTools} />
          </DashboardShell>
        }
      />
      <Route
        path="usage"
        element={
          <DashboardShell
            title="USAGE"
            subtitle="Quota and API usage."
            backTo={{ label: 'Dashboard', path: '/dashboard' }}
          >
            <UsagePanel />
          </DashboardShell>
        }
      />
    </Routes>
  )
}
